import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Briefcase, ArrowLeft, DollarSign, Info } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const AdSenseConfigPage = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({
    enabled: false,
    client_id: '',
    ad_slots: {
      homepage_top: '',
      jobs_list_between: '',
      job_detail_sidebar: '',
      anuncios_sidebar: ''
    }
  });

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    if (user.role !== 'admin') {
      toast.error('Acceso denegado: Solo administradores pueden acceder');
      navigate('/dashboard');
      return;
    }
    fetchConfig();
  }, [user]);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API}/adsense/config`);
      if (response.ok) {
        const data = await response.json();
        if (data.client_id || data.ad_slots) {
          setConfig({
            enabled: data.enabled || false,
            client_id: data.client_id || '',
            ad_slots: { ...config.ad_slots, ...data.ad_slots }
          });
        }
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/adsense/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify(config),
      });

      if (response.ok) {
        toast.success('Configuración de AdSense guardada');
        // Update environment variable info
        if (config.enabled && config.client_id) {
          toast.info('Recuerda agregar el script de AdSense al HTML', {
            duration: 5000
          });
        }
      } else {
        toast.error('Error al guardar configuración');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Error al guardar configuración');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <Briefcase className="w-7 h-7 text-red-600" />
              <span className="text-xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">
                JobSuiza
              </span>
            </Link>

            <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <DollarSign className="w-10 h-10 text-red-600" />
            <h1 className="text-4xl font-bold">Configuración de Google AdSense</h1>
          </div>
          <p className="text-lg text-slate-600">Monetiza tu plataforma con anuncios de Google</p>
        </div>

        {/* Instructions Card */}
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="p-6">
            <div className="flex items-start space-x-3">
              <Info className="w-6 h-6 text-blue-600 mt-1" />
              <div>
                <h3 className="font-bold text-blue-900 mb-2">Cómo configurar Google AdSense:</h3>
                <ol className="list-decimal list-inside space-y-1 text-blue-800">
                  <li>Crea una cuenta en <a href="https://www.google.com/adsense" target="_blank" rel="noopener noreferrer" className="underline">Google AdSense</a></li>
                  <li>Obtén tu Client ID (formato: ca-pub-XXXXXXXXXXXXXXXX)</li>
                  <li>Crea espacios publicitarios (ad slots) en tu panel de AdSense</li>
                  <li>Copia los códigos de slot para cada posición</li>
                  <li>Pega los códigos aquí y activa los anuncios</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Configuration Form */}
        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle>Configuración de Anuncios</CardTitle>
            <CardDescription>Gestiona dónde y cómo se muestran los anuncios</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable/Disable Switch */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label htmlFor="enabled" className="text-base font-semibold">Activar Anuncios</Label>
                <p className="text-sm text-slate-600">Mostrar anuncios de AdSense en todo el sitio</p>
              </div>
              <Switch
                id="enabled"
                checked={config.enabled}
                onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
                data-testid="enable-ads-switch"
              />
            </div>

            {/* Client ID */}
            <div>
              <Label htmlFor="client_id">Google AdSense Client ID</Label>
              <Input
                id="client_id"
                placeholder="ca-pub-XXXXXXXXXXXXXXXX"
                value={config.client_id}
                onChange={(e) => setConfig({ ...config, client_id: e.target.value })}
                data-testid="client-id-input"
              />
              <p className="text-xs text-slate-500 mt-1">Formato: ca-pub-XXXXXXXXXXXXXXXX</p>
            </div>

            {/* Ad Slots */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Espacios Publicitarios (Ad Slots)</h3>
              
              <div>
                <Label htmlFor="homepage_top">Homepage - Banner Superior</Label>
                <Input
                  id="homepage_top"
                  placeholder="1234567890"
                  value={config.ad_slots.homepage_top}
                  onChange={(e) => setConfig({ 
                    ...config, 
                    ad_slots: { ...config.ad_slots, homepage_top: e.target.value }
                  })}
                  data-testid="homepage-top-slot"
                />
              </div>

              <div>
                <Label htmlFor="jobs_list_between">Lista de Trabajos - Entre ofertas</Label>
                <Input
                  id="jobs_list_between"
                  placeholder="1234567890"
                  value={config.ad_slots.jobs_list_between}
                  onChange={(e) => setConfig({ 
                    ...config, 
                    ad_slots: { ...config.ad_slots, jobs_list_between: e.target.value }
                  })}
                  data-testid="jobs-list-slot"
                />
              </div>

              <div>
                <Label htmlFor="job_detail_sidebar">Detalle de Trabajo - Sidebar</Label>
                <Input
                  id="job_detail_sidebar"
                  placeholder="1234567890"
                  value={config.ad_slots.job_detail_sidebar}
                  onChange={(e) => setConfig({ 
                    ...config, 
                    ad_slots: { ...config.ad_slots, job_detail_sidebar: e.target.value }
                  })}
                  data-testid="job-detail-slot"
                />
              </div>
            </div>

            {/* Save Button */}
            <Button 
              onClick={handleSave} 
              className="w-full bg-red-600 hover:bg-red-700"
              data-testid="save-config-btn"
            >
              Guardar Configuración
            </Button>

            {/* Preview Note */}
            {config.enabled && config.client_id && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-800">
                  ✅ Los anuncios se mostrarán en las posiciones configuradas una vez que Google apruebe tu cuenta.
                  Puede tardar 24-48 horas.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Card */}
        <Card className="mt-6 bg-slate-50">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-2">¿Necesitas ayuda?</h3>
            <p className="text-sm text-slate-600 mb-3">
              Los anuncios aparecerán automáticamente en las posiciones que configures. Asegúrate de:
            </p>
            <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
              <li>Tener una cuenta de AdSense aprobada</li>
              <li>Haber verificado tu sitio web en AdSense</li>
              <li>Esperar 24-48 horas para que los anuncios se activen</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdSenseConfigPage;
